import { action, observable, computed } from 'mobx';
import { post, put } from '../../../services/request';

export default class OffersStore {
  @observable viewModel = undefined;
  @observable selectedOffer = undefined;

  @computed get emails() {
    if (!this.viewModel) {
      return [];
    }
    return this.viewModel.offers
      .filter((a) => !a.buyerIsBlocked)
      .map((o) => o.buyer.buyer_email);
  }

  @computed get isOfferSelected() {
    return !!this.selectedOffer;
  }

  @computed get hasWinner() {
    return !!this.viewModel && this.viewModel.hasAwardedContract;
  }

  @computed get hasAccessibleOffers() {
    return this.accessibleOffers.length > 0;
  }

  @computed get accessibleOffers() {
    if (!this.viewModel) return [];

    return (this.viewModel.offers || []).filter((offer) =>
      this.root.user.hasPermissions(offer.agentData)
    );
  }

  constructor({ root }) {
    this.root = root;
  }

  @action sortOffersByBuyerName(desc) {
    if (!this.viewModel.offers || this.viewModel.offers.length === 0) {
      return;
    }
    this.viewModel.offers = this.viewModel.offers.concat([]).sort((a, b) => {
      const firstValue = a.buyer.buyer_lastName;
      const secondValue = b.buyer.buyer_lastName;
      if (firstValue === secondValue) {
        return 0;
      }
      if (desc) {
        return firstValue > secondValue ? 1 : -1;
      } else {
        return firstValue > secondValue ? -1 : 1;
      }
    });
  }

  @action getByBuyerId(buyerId) {
    if (!this.viewModel) {
      return null;
    }
    return this.viewModel.offers.filter((o) => o.buyerId === buyerId)[0];
  }

  @action update(viewModel) {
    this.viewModel = viewModel;
    this.sortOffersByBuyerName(true);
  }

  @action select(offer) {
    this.selectedOffer = offer;
  }

  @action reset() {
    this.viewModel = undefined;
    this.selectedOffer = undefined;
  }

  @action async register({ buyerData, offerData, agentData }) {
    const url = `/api/v2/registrations/${this.root.saleId}`;
    const { user, isMls, saleAgentData } = this.root;
    const agent = isMls
      ? agentData
        ? agentData
        : user.agentData()
      : saleAgentData;

    await this.root.ui.loading.start(
      async () =>
        await post(
          url,
          {
            buyerData,
            offerData,
            agentData: agent,
          },
          this.root.user.accessToken
        )
    );
  }

  @action async updateBuyer({
    email,
    phoneNumber,
    salutation,
    title,
    firstName,
    lastName,
    locale,
    offerData,
    agentData,
    triggerNotification = false,
  }) {
    await put(
      `/api/v1/offers/${this.selectedOffer.buyerId}/buyer`,
      {
        saleId: this.root.saleId,
        email,
        phoneNumber,
        salutation,
        title,
        firstName,
        lastName,
        locale,
        offerData,
        triggerNotification,
        agentData,
      },
      this.root.user.accessToken
    );
  }

  @action async updateContact({ email, phoneNumber }) {
    await post(
      `/api/v1/offers/${this.selectedOffer.buyerId}/contact`,
      {
        saleId: this.root.saleId,
        email,
        phoneNumber,
      },
      this.root.user.accessToken
    );
  }

  @action async uploadOffer(offer) {
    const { buyerId = null } = this.selectedOffer || {};
    const { user, isMls, saleAgentData } = this.root;
    const agentData = isMls ? user.agentData() : saleAgentData;
    await post(
      `/api/v1/offers/upload`,
      {
        buyerId: buyerId,
        saleId: this.root.saleId,
        agentData: agentData,
        ...offer,
      },
      this.root.user.accessToken
    );
  }

  @action increaseOffer(offer) {
    const { buyerId = null } = this.selectedOffer || {};
    put(
      `/api/v1/offers/${buyerId}/increase-on-behalf`,
      { saleId: this.root.saleId, ...offer },
      this.root.user.accessToken
    ).then(this.root.success, this.root.failed);
  }

  @action block({ reason }) {
    put(
      `/api/v1/registrations/${this.selectedOffer.buyerId}/block`,
      { saleId: this.selectedOffer.saleId, reason },
      this.root.user.accessToken
    ).then(this.root.success, this.root.failed);
  }

  @action confirmOnBehalf() {
    this._offer('confirm-on-behalf');
  }

  @action approve() {
    this._offer('approve');
  }

  @action refuse(reason, offer) {
    this._offer('refuse', { reason, ...offer });
  }

  @action award() {
    put(
      `/api/v1/contracts/${this.selectedOffer.buyerId}/award`,
      { saleId: this.selectedOffer.saleId },
      this.root.user.accessToken
    ).then(this.root.success, this.root.failed);
  }

  @action accept() {
    this._offer('accept');
  }

  @action reject() {
    this._offer('reject');
  }

  @action approveRegistration(offerData) {
    this._registration('approve', { offerData: offerData });
  }

  @action refuseRegistration() {
    this._registration('refuse');
  }

  _registration(action, offer = {}) {
    const url = `/api/v2/registrations/${this.selectedOffer.saleId}/${this.selectedOffer.buyerId}/${action}`;
    post(url, offer, this.root.user.accessToken).then(
      this.root.success,
      this.root.failed
    );
  }

  _offer(action, data = {}) {
    put(
      `/api/v1/offers/${this.selectedOffer.buyerId}/${action}`,
      { saleId: this.selectedOffer.saleId, ...data },
      this.root.user.accessToken
    ).then(this.root.success, this.root.failed);
  }
}



// WEBPACK FOOTER //
// ./src/app/containers/Sale/stores/OffersStore.js